import { CircleCheckBig, CircleX } from 'lucide-preact';
import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { api } from '../../services/api.js';

interface VerifyEmailPageProps {
  path?: string;
}

type Status = 'verifying' | 'success' | 'error';

export function VerifyEmailPage(_props: VerifyEmailPageProps) {
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  // Verify the token on mount.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      route('/account/register');
      return;
    }
    api
      .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setStatus('success'))
      .catch(() => {
        // The API client surfaces only a generic message ("Request failed"),
        // so show a clear, spec-aligned message for every failure (invalid,
        // expired, already-used, or network error).
        setErrorMessage(
          'This verification link is invalid or has expired. Please request a new one.',
        );
        setStatus('error');
      });
  }, []);

  // Auto-redirect to login 3s after a successful verification.
  useEffect(() => {
    if (status !== 'success') return;
    const id = setTimeout(() => route('/account/login'), 3000);
    return () => clearTimeout(id);
  }, [status]);

  return (
    <div className="page-fill bg-bg flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        {status === 'verifying' && (
          <>
            <h1 className="text-2xl font-bold text-text mb-2">Verifying your email…</h1>
            <p className="text-gray-400">Hold on a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="text-3xl font-bold text-text mb-2 flex items-center justify-center gap-2">
              <CircleCheckBig size={28} aria-hidden="true" />
              Email Verified
            </h1>
            <p className="text-gray-400 mb-8">
              Your email is verified. Redirecting you to login in a few seconds…
            </p>
            <Button className="w-full" onClick={() => route('/account/login')}>
              Go to login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-3xl font-bold text-text mb-2 flex items-center justify-center gap-2">
              <CircleX size={28} aria-hidden="true" />
              Verification Failed
            </h1>
            <p className="text-red-400 mb-8">{errorMessage}</p>
            <Button className="w-full" onClick={() => route('/account/forgot-password')}>
              Request new link
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
